import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { RiskEntry, RiskImage } from 'src/app/classes/risks/risk-group';
import { Metric } from 'src/app/interfaces/api/metric';
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';
import { ButtonComponent, ChipComponent } from 'ui-library';

@Component({
  selector: 'app-risk-detail-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatChipsModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    RouterLink,
    TimeFormatPipe,
    ButtonComponent,
    ChipComponent,
  ],
  templateUrl: './risk-detail-panel.component.html',
  styleUrls: ['./risk-detail-panel.component.scss'],
})
export class RiskDetailPanelComponent implements AfterViewInit, OnChanges {
  @Input() image!: RiskImage;
  @Input() selectedNumber: number = 1;
  @Input() actionsByRisk: Record<string, { id: string; title: string }[]> = {};

  @Output() select: EventEmitter<number> = new EventEmitter();
  @Output() showImage: EventEmitter<void> = new EventEmitter();
  @Output() report: EventEmitter<void> = new EventEmitter();
  @Output() linkAction: EventEmitter<void> = new EventEmitter();
  @Output() openAction: EventEmitter<string> = new EventEmitter();
  @Output() commentEdit: EventEmitter<void> = new EventEmitter();

  @ViewChild('tabScroll') tabScrollRef?: ElementRef<HTMLElement>;
  @ViewChildren('tabEl') tabEls?: QueryList<ElementRef<HTMLElement>>;
  canScrollLeft = false;
  canScrollRight = false;

  get isMulti(): boolean {
    return (this.image?.risks?.length ?? 0) > 1;
  }

  get selectedEntry(): RiskEntry | undefined {
    if (!this.image?.risks?.length) return undefined;
    return this.image.risks.find(r => r.number === this.selectedNumber) ?? this.image.risks[0];
  }

  get selectedRisk(): Metric | undefined {
    return this.selectedEntry?.metric;
  }

  ngAfterViewInit(): void {
    setTimeout(() => { this.scrollSelectedTabIntoView(); this.updateArrows(); });
  }

  ngOnChanges(): void {
    // selectedNumber may have changed (e.g. from a map fan-icon click) — bring its tab into view
    setTimeout(() => { this.scrollSelectedTabIntoView(); this.updateArrows(); });
  }

  riskName(entry: RiskEntry): string {
    return entry.metric.payload['displayTitle'] || entry.metric.payload.title || '';
  }

  onSelect(entry: RiskEntry) {
    if (entry.number !== this.selectedNumber) this.select.emit(entry.number);
  }

  updateArrows(): void {
    const el = this.tabScrollRef?.nativeElement;
    if (!el) {
      this.canScrollLeft = this.canScrollRight = false;
      return;
    }
    this.canScrollLeft = el.scrollLeft > 1;
    this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  }

  scrollTabs(direction: number): void {
    const el = this.tabScrollRef?.nativeElement;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.7, behavior: 'smooth' });
    setTimeout(() => this.updateArrows(), 350);
  }

  private scrollSelectedTabIntoView(): void {
    const container = this.tabScrollRef?.nativeElement;
    const tabs = this.tabEls?.toArray();
    if (!container || !tabs?.length) return;
    const idx = this.image?.risks?.findIndex(r => r.number === this.selectedNumber) ?? -1;
    const el = tabs[idx]?.nativeElement;
    if (!el) return;
    const c = container.getBoundingClientRect();
    const e = el.getBoundingClientRect();
    if (e.left < c.left) container.scrollLeft -= (c.left - e.left) + 8;
    else if (e.right > c.right) container.scrollLeft += (e.right - c.right) + 8;
  }
}
